import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { formatCurrency, formatShortDate } from '@/utils/common/formatting';
import { IAdminTransaction } from '@/types/admin/transactions';
import {
  getPaymentStatusColor,
  isPayment as checkIsPayment,
  getTransactionAmountColor,
  getTransactionTypeLabel,
  hasTransactionDetails,
} from '@/utils/admin/transactions';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useTransactionPalette } from '@/hooks/admin/useTransactionPalette';
import { GlassCard } from '@/components/shared/redesign';

interface TransactionListProps {
  transactions: IAdminTransaction[];
  maxRows?: number; // If undefined, shows first 5; if 0 or null, shows all
}

export const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  maxRows,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string | number>>(new Set());
  const styles = useStyles();
  const { admin, redesign, semantic } = useAdminColors();
  const transactionPalette = useTransactionPalette();

  const statusPalette = useMemo(
    () => ({
      success: semantic.status.success,
      error: semantic.status.error,
      pending: semantic.status.warning,
      voided: semantic.text.muted,
      fallback: redesign.text.dim,
    }),
    [semantic.status.success, semantic.status.error, semantic.status.warning, semantic.text.muted, redesign.text.dim],
  );

  const amountPalette = useMemo(
    () => ({ payment: transactionPalette.payment, charge: transactionPalette.charge }),
    [transactionPalette.payment, transactionPalette.charge],
  );

  // If maxRows is undefined, default to 5; if 0 or null, show all
  const displayTransactions = maxRows === undefined
    ? transactions.slice(0, 5)
    : maxRows > 0
      ? transactions.slice(0, maxRows)
      : transactions;

  const toggleExpand = (id: string | number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  const renderTransaction = ({ item }: { item: IAdminTransaction }) => {
    const isExpanded = expandedIds.has(item.transactionId);
    const isPayment = checkIsPayment(item);
    const hasDetails = hasTransactionDetails(item);
    const amountColor = getTransactionAmountColor(item, amountPalette);

    const typeBadgeColor = isPayment ? transactionPalette.payment : transactionPalette.charge;

    return (
      <GlassCard
        style={styles.cardSpacing}
        padding={16}
        interactive={hasDetails}
        onPress={hasDetails ? () => toggleExpand(item.transactionId) : undefined}
      >
        {/* Header */}
        <View style={styles.cardHeader}>
          <View style={styles.headerLeft}>
            <Text style={styles.date}>{formatShortDate(item.transactionDate)}</Text>
            <View
              style={[
                styles.typeBadge,
                { backgroundColor: typeBadgeColor + '20' },
              ]}
            >
              <Text style={[styles.typeBadgeText, { color: redesign.text.primary }]}>
                {getTransactionTypeLabel(item)}
              </Text>
            </View>
          </View>
          <Text style={[styles.amount, { color: amountColor, fontWeight: '700' }]}>
            {formatCurrency(Math.abs(item.amount))}
          </Text>
        </View>

        {/* Description */}
        <Text style={styles.description} numberOfLines={isExpanded ? undefined : 2}>
          {item.description}
        </Text>

        {item.arCodeDescription && (
          <Text style={styles.arCode}>{item.arCodeDescription}</Text>
        )}

        {/* Expanded Details */}
        {isExpanded && hasDetails && (
          <View style={styles.expandedSection}>
            {item.paidByFirstName && item.paidByLastName && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Paid By:</Text>
                <Text style={styles.detailValue}>
                  {item.paidByFirstName} {item.paidByLastName}
                </Text>
              </View>
            )}

            {item.paymentSettlementStatus && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Status:</Text>
                <View
                  style={[
                    styles.statusBadge,
                    { backgroundColor: getPaymentStatusColor(item.paymentSettlementStatus, statusPalette) + '20' },
                  ]}
                >
                  <Text
                    style={[
                      styles.statusText,
                      { color: getPaymentStatusColor(item.paymentSettlementStatus, statusPalette) },
                    ]}
                  >
                    {item.paymentSettlementStatus}
                  </Text>
                </View>
              </View>
            )}

            {item.paymentConvenienceFee && item.paymentConvenienceFee > 0 && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Convenience Fee:</Text>
                <Text style={styles.detailValue}>
                  {formatCurrency(item.paymentConvenienceFee)}
                </Text>
              </View>
            )}

            {item.postMonth && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>Post Month:</Text>
                <Text style={styles.detailValue}>
                  {formatShortDate(item.postMonth)}
                </Text>
              </View>
            )}

            {item.arCode && (
              <View style={styles.detailRow}>
                <Text style={styles.detailLabel}>AR Code:</Text>
                <Text style={styles.detailValue}>
                  {item.arCode}
                </Text>
              </View>
            )}
          </View>
        )}

        {hasDetails && (
          <View style={styles.expandIndicator}>
            <Text style={styles.expandIndicatorText}>
              {isExpanded ? 'Tap to collapse' : 'Tap for details'}
            </Text>
            {isExpanded ? (
              <SVG_COMPONENTS.CHEVRON_UP width={12} height={12} color={admin.dataCell.textMuted} />
            ) : (
              <SVG_COMPONENTS.CHEVRON_RIGHT width={12} height={12} color={admin.dataCell.textMuted} />
            )}
          </View>
        )}
      </GlassCard>
    );
  };

  return (
    <FlatList
      data={displayTransactions}
      renderItem={renderTransaction}
      keyExtractor={(item) => String(item.transactionId)}
      ItemSeparatorComponent={() => <View style={styles.separator} />}
      showsVerticalScrollIndicator={false}
      style={styles.list}
    />
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      list: {
        flex: 1,
      },
      cardSpacing: {
        marginVertical: 4,
      },
      cardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      },
      headerLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flex: 1,
      },
      date: {
        fontSize: 13,
        fontWeight: '600',
        color: components.admin.dataCell.textMuted,
      },
      typeBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 4,
      },
      typeBadgeText: {
        fontSize: 10,
        fontWeight: '600',
      },
      amount: {
        fontSize: 18,
      },
      description: {
        fontSize: 14,
        color: colors.text.primary,
        marginBottom: 4,
      },
      arCode: {
        fontSize: 12,
        color: components.admin.dataCell.textMuted,
        marginBottom: 8,
      },
      expandedSection: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: components.admin.dataTable.border,
      },
      detailRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 6,
      },
      detailLabel: {
        fontSize: 13,
        color: components.admin.dataCell.textMuted,
        fontWeight: '500',
      },
      detailValue: {
        fontSize: 13,
        color: colors.text.primary,
        fontWeight: '600',
      },
      statusBadge: {
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 4,
      },
      statusText: {
        fontSize: 11,
        fontWeight: '600',
      },
      expandIndicator: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 8,
        gap: 4,
      },
      expandIndicatorText: {
        fontSize: 11,
        color: components.admin.dataCell.textMuted,
      },
      separator: {
        height: 8,
      },
    }),
  );
